import { AUTO_LINE_ORDER, CHART_NAME_MAX_LENGTH, MAX_ACTIVE_SERIES, MAX_CHARTS } from "../constants";
import type { LineStyleKey, SeriesKey, Chart } from "../types";

/**
 * Pure operations on the list of trends. Each returns a new array, never
 * mutates its input, and drops any trend left with no series.
 */

type MakeId = () => string;

const dropEmpty = (charts: Chart[]) => charts.filter((t) => t.seriesIds.length > 0);

const unique = (ids: string[]) => Array.from(new Set(ids));

/** Every different series on the page, in trend order. */
export function activeSeriesIds(charts: Chart[]): string[] {
  return unique(charts.flatMap((t) => t.seriesIds));
}

/** Whether a series can go on the page: it's already there, or there's room for one more. */
export function canActivate(charts: Chart[], displayId: string): boolean {
  const active = activeSeriesIds(charts);
  return active.includes(displayId) || active.length < MAX_ACTIVE_SERIES;
}

export function seriesKey(chartId: string, displayId: string): SeriesKey {
  return `${chartId}:${displayId}`;
}

/** Moves a series from one trend to another (no-op when they're the same). */
export function moveSeries(charts: Chart[], fromId: string | null, displayId: string, toId: string): Chart[] {
  if (fromId === toId || !charts.some((t) => t.id === toId)) return charts;

  return dropEmpty(
    charts.map((t) => {
      if (t.id === toId) {
        return t.seriesIds.includes(displayId) ? t : { ...t, seriesIds: [...t.seriesIds, displayId] };
      }
      if (t.id === fromId) return { ...t, seriesIds: t.seriesIds.filter((id) => id !== displayId) };
      return t;
    }),
  );
}

/**
 * Gives a series its own new trend at the end, taking it out of its old trend.
 * Does nothing at the trend limit, when the series is already alone, or when
 * it's a new series and the page already has six.
 */
export function newChartFrom(charts: Chart[], displayId: string, fromId: string | null, makeId: MakeId): Chart[] {
  if (charts.length >= MAX_CHARTS || !canActivate(charts, displayId)) return charts;
  const from = fromId ? charts.find((t) => t.id === fromId) : undefined;
  if (from && from.seriesIds.length === 1) return charts;

  const rest = charts.map((t) =>
    t.id === fromId ? { ...t, seriesIds: t.seriesIds.filter((id) => id !== displayId) } : t,
  );
  return dropEmpty([...rest, { id: makeId(), seriesIds: [displayId] }]);
}

/**
 * Gives a series its own new trend at `index` (before the trend now there),
 * taking it out of its old trend. When it's the only series in its trend, that
 * trend moves to `index` instead, keeping its id and settings. Does nothing at
 * the trend limit, or for a new series when the page already has six.
 */
export function newChartAt(
  charts: Chart[],
  displayId: string,
  fromId: string | null,
  index: number,
  makeId: MakeId,
): Chart[] {
  if (index < 0 || index > charts.length) return charts;
  const from = fromId ? charts.find((t) => t.id === fromId) : undefined;

  if (from && from.seriesIds.length === 1) {
    const fromIndex = charts.indexOf(from);
    const out = charts.filter((t) => t !== from);
    out.splice(fromIndex < index ? index - 1 : index, 0, from);
    return out;
  }

  if (charts.length >= MAX_CHARTS || !canActivate(charts, displayId)) return charts;
  const rest = charts.map((t) =>
    t.id === fromId ? { ...t, seriesIds: t.seriesIds.filter((id) => id !== displayId) } : t,
  );
  rest.splice(index, 0, { id: makeId(), seriesIds: [displayId] });
  return dropEmpty(rest);
}

/**
 * Splits one trend into a trend per series, right after it. When that would go
 * past the limit, the first series stay together in the original trend.
 */
export function splitChart(charts: Chart[], chartId: string, makeId: MakeId): Chart[] {
  const room = MAX_CHARTS - charts.length;
  const out: Chart[] = [];

  for (const t of charts) {
    if (t.id !== chartId || room <= 0 || t.seriesIds.length < 2) {
      out.push(t);
      continue;
    }
    const moveCount = Math.min(room, t.seriesIds.length - 1);
    const keep = t.seriesIds.slice(0, t.seriesIds.length - moveCount);
    const moved = t.seriesIds.slice(keep.length);
    out.push({ ...t, seriesIds: keep });
    // The pieces keep the date range the chart showed.
    moved.forEach((id) =>
      out.push({ id: makeId(), seriesIds: [id], ...(t.rangeOverride ? { rangeOverride: t.rangeOverride } : {}) }),
    );
  }
  return out;
}

/** One trend per series, up to the limit. The first trend keeps its id. */
export function splitAll(charts: Chart[], makeId: MakeId): Chart[] {
  const all = unique(charts.flatMap((t) => t.seriesIds)).slice(0, MAX_CHARTS);
  return all.map((id, i) => ({ id: i === 0 && charts[0] ? charts[0].id : makeId(), seriesIds: [id] }));
}

/**
 * A merged trend: `into`'s id and settings with these series. Its date range
 * goes back to the page's, as a merge is for comparing the series on one axis.
 */
function merged(into: Chart, seriesIds: string[]): Chart {
  const chart: Chart = { ...into, seriesIds: unique(seriesIds) };
  delete chart.rangeOverride;
  return chart;
}

/** Every series in one trend, keeping the first trend's id and settings. */
export function mergeAll(charts: Chart[]): Chart[] {
  if (charts.length < 2) return charts;
  const seriesIds = charts.flatMap((t) => t.seriesIds);
  return [merged(charts[0], seriesIds)];
}

/** Folds a trend into the one above it. */
export function mergeUp(charts: Chart[], chartId: string): Chart[] {
  const i = charts.findIndex((t) => t.id === chartId);
  if (i < 1) return charts;

  const into = merged(charts[i - 1], [...charts[i - 1].seriesIds, ...charts[i].seriesIds]);
  return [...charts.slice(0, i - 1), into, ...charts.slice(i + 1)];
}

export function removeSeries(charts: Chart[], chartId: string, displayId: string): Chart[] {
  return dropEmpty(
    charts.map((t) => (t.id === chartId ? { ...t, seriesIds: t.seriesIds.filter((id) => id !== displayId) } : t)),
  );
}

/** Adds a series to a trend, unless it's a new series and the page already has six. */
export function addSeries(charts: Chart[], chartId: string, displayId: string): Chart[] {
  if (!canActivate(charts, displayId)) return charts;
  return charts.map((t) =>
    t.id === chartId && !t.seriesIds.includes(displayId) ? { ...t, seriesIds: [...t.seriesIds, displayId] } : t,
  );
}

export function deleteChart(charts: Chart[], chartId: string): Chart[] {
  return charts.filter((t) => t.id !== chartId);
}

/** A series' line style: the user's pick, or Auto by its position in the trend. */
export function resolveLineStyle(
  chart: Chart,
  displayId: string,
  custom: Record<string, LineStyleKey>,
): LineStyleKey {
  if (custom[displayId]) return custom[displayId];
  const index = Math.max(0, chart.seriesIds.indexOf(displayId));
  return AUTO_LINE_ORDER[index % AUTO_LINE_ORDER.length];
}

/** A trend's heading: the name the user gave it, else its series' name when it has one, else "Chart N". */
export function chartTitle(chart: Chart, index: number, nameOf: (displayId: string) => string): string {
  if (chart.name) return chart.name;
  return chart.seriesIds.length === 1 ? nameOf(chart.seriesIds[0]) : `Chart ${index + 1}`;
}

/** Names a trend, or with an empty name, drops the name it was given. */
export function renameChart(charts: Chart[], chartId: string, name: string | null): Chart[] {
  const trimmed = name?.trim().slice(0, CHART_NAME_MAX_LENGTH);
  return charts.map((t) => {
    if (t.id !== chartId) return t;
    if (trimmed) return { ...t, name: trimmed };
    const unnamed = { ...t };
    delete unnamed.name;
    return unnamed;
  });
}
